import React, { useMemo } from 'react';
import { AppBar, Tabs } from '@material-ui/core';
import { Container, Row } from 'react-bootstrap';
import { twMerge } from 'tailwind-merge';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { WorkflowType } from 'models/workflow/WorkflowType';

import { ResponseList } from 'models/Response';
import { TypeMaster } from 'models/type/TypeMaster';

const WORKFLOW_TYPE_COLORS = [
    '#6A5C70', // Dark grayish purple
    '#FF6B80', // Muted mauve
    '#FF5A60', // Dusty rose
    '#FF8570', // Peach
    '#FFA070', // Light peach
    '#C27AFF', // Soft lavender
    '#FF70A5', // Pink
    '#50BFFF', // Light blue
    '#30AAFF', // Sky blue
    '#0095FF', // Ocean blue
    '#A455DD', // Wisteria purple
    '#FF55A0', // Rose pink
    '#FF85A5', // Pale pink
    '#FFA090', // Shell pink
    '#FF70FF', // Light purple
    '#805580', // Dusky purple
    '#FF7070', // Vintage rose
    '#E04D70', // Mauve taupe
    '#6B5C65', // Warm gray
    '#7080E0' // Steel blue gray
];

function getRandomInteger(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export type WorkflowTypeListProps = {
    currentPage: number;
    allWorkflowCountLabel: string;
    workflowTypes: WorkflowType[];
    isPendingWorkflowInbox?: boolean;
    isFetchingWorkflowInbox?: boolean;
    className?: string;
    onChangeWorkflowType?: <T extends WorkflowType | 'initial'>(
        workflowType: T,
        index: T extends WorkflowType ? number : undefined
    ) => void;
};

export const WorkflowTypeList = (props: WorkflowTypeListProps) => {
    const {
        currentPage,
        allWorkflowCountLabel,
        workflowTypes,
        isPendingWorkflowInbox,
        isFetchingWorkflowInbox,
        className,
        onChangeWorkflowType
    } = props;

    const { data: workflowTypeConfigs = [] } = useQuery({
        queryKey: ['workflow-type-master', { sid: '*' }],
        queryFn: ({ signal }) =>
            axios.get<ResponseList<TypeMaster>>(
                '/v2/configuration/workflow/type',
                {
                    signal,
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            'WF_APPLICATION'
                        )}`
                    },
                    params: {
                        sid: '*'
                    }
                }
            ),
        // @ts-ignore
        select: response => response.data.result_list,
        retry: 1
    });

    const sortedWorkflowTypesWithColor = useMemo(() => {
        const workflowTypeConfigMap = new Map(
            workflowTypeConfigs.map(typeConfig => [
                typeConfig.workflowtypeid,
                typeConfig
            ])
        );

        return workflowTypes
            .filter(type => (Number(type.count) || 0) > 0)
            .map(type => {
                const workflowTypeConfig = workflowTypeConfigMap.get(
                    type.wftype
                ) as TypeMaster | undefined;

                const badgeColor =
                    workflowTypeConfig?.background_color ||
                    WORKFLOW_TYPE_COLORS[
                    getRandomInteger(0, WORKFLOW_TYPE_COLORS.length - 1)
                    ];
                const label = workflowTypeConfig?.workflowtypename
                    ? `${workflowTypeConfig.workflowtypename} (${type.count} รายการ)`
                    : (type.wftypeName != null
                        ? type.wftypeName
                        : type.wftype) + ` (${type.count} รายการ)`;

                return { ...type, label, badgeColor };
            })
            .sort((a, b) => {
                const aName = a.wftypeName || a.wftype;
                const bName = b.wftypeName || b.wftype;

                if (aName < bName) return -1;
                if (aName > bName) return 1;
                return 0;
            });
    }, [workflowTypes, workflowTypeConfigs]);

    if (sortedWorkflowTypesWithColor.length === 0) {
        return null;
    }

    return (
        <Row
            className={twMerge(
                'z-[900] mx-[15px] mt-2',
                isPendingWorkflowInbox && 'disable-div',
                className
            )}
        >
            <AppBar
                position="static"
                color="inherit"
                className="z-0 shadow-none"
            >
                <Tabs
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                    value={currentPage}
                    className="z-10"
                >
                    <div
                        className="nav-item"
                        onClick={() =>
                            onChangeWorkflowType?.('initial', undefined)
                        }
                    >
                        <a className="pointer">
                            <div
                                className={twMerge(
                                    'trapezoid trapezoid-purple w-fit',
                                    currentPage === 0 && 'active'
                                )}
                            >
                                <span>
                                    <i
                                        className="fa fa-search"
                                        aria-hidden="true"
                                    />
                                    {isFetchingWorkflowInbox
                                        ? 'เอกสาร (0 รายการ)'
                                        : allWorkflowCountLabel}
                                </span>
                            </div>
                        </a>
                    </div>
                    {sortedWorkflowTypesWithColor.map((workflowType, index) => (
                        <div
                            key={index}
                            className="nav-item"
                            onClick={() =>
                                onChangeWorkflowType?.(workflowType, index)
                            }
                        >
                            <a className="pointer disabled">
                                <div
                                    className={twMerge(
                                        'trapezoid w-fit transition-colors duration-700',
                                        currentPage === index + 1 && 'active'
                                    )}
                                    style={{
                                        borderBottom: `32px solid ${workflowType.badgeColor}`
                                    }}
                                >
                                    <span>
                                        <i
                                            className="fa fa-search"
                                            aria-hidden="true"
                                        />
                                        {isFetchingWorkflowInbox
                                            ? 'เอกสาร (0 รายการ)'
                                            : workflowType.label}
                                    </span>
                                </div>
                            </a>
                        </div>
                    ))}
                </Tabs>
            </AppBar>
        </Row>
    );
};
